import {Component, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ToasterConfig} from 'angular2-toaster';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {MatDrawerMode, MatSidenav} from '@angular/material/sidenav';
import {ResizeAnimation, ResizeService} from '../../shared/resize.service';
import {MenuService} from '../../shared/menu.service';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {LoginService} from '../../shared/login.service';

const MEDIUM_WIDTH_SIZE = 1279; // window width breakpoint for side nav mode change
const RESIZE_DELAY = 40; // in milliseconds

@Component({
    selector: 'app-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss']
})
export class ContentComponent implements OnInit, OnDestroy {

    private readonly destroyed = new Subject();
    @ViewChild('entityTreePanel') private entityTreePanel: MatSidenav;
    private resizeAnimation: ResizeAnimation;

    sideNavMode: BehaviorSubject<MatDrawerMode>;
    menuEnabled: Observable<boolean>;
    menuShown: Observable<boolean>;
    showEntityTree: Observable<boolean>;
    toasterConfig: ToasterConfig;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly resizeService: ResizeService,
                private readonly menuService: MenuService,
                private readonly loginService: LoginService) {
        this.toasterConfig = new ToasterConfig({
            animation: 'fade',
            positionClass: 'toast-top-center',
            limit: 1,
            tapToDismiss: true,
            showCloseButton: false,
            newestOnTop: true,
            timeout: cfg.toasterTimeout,
            mouseoverTimerStop: false
        });
        this.sideNavMode = new BehaviorSubject<MatDrawerMode>('side');
        this.resizeAnimation = null;
    }

    ngOnInit() {
        this.menuEnabled = combineLatest([this.loginService.userProfile, this.menuService.menuEnabledStatus]).pipe(
            map(([profile, menuEnabled]) => !!profile?.canOperate && menuEnabled),
            takeUntil(this.destroyed)
        );
        this.menuShown = combineLatest([this.loginService.userProfile, this.menuService.menuShowStatus]).pipe(
            map(([profile, menuShown]) => !!profile?.canOperate && menuShown),
            takeUntil(this.destroyed)
        );
        this.showEntityTree = combineLatest([this.menuService.treeEnabledStatus, this.menuService.treeShowStatus]).pipe(
            map(([treeEnabled, treeShow]) => treeEnabled && treeShow),
            takeUntil(this.destroyed)
        );
        this.checkWidth();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    // noinspection JSUnusedLocalSymbols (see https://stackoverflow.com/questions/50405930/aot-angular-6-directive-somecomponent-expected-0-arguments-but-got-1-for)
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.checkWidth();
    }

    onEntityTreeOpenChanged(event: boolean) {
        if (this.resizeAnimation) {
            this.resizeAnimation.stop();
            this.resizeAnimation = null;
        }
        this.resizeService.resize(['content']);
    }

    onEntityTreeAnimationStart() {
        if (!this.resizeAnimation && this.sideNavMode.getValue() === 'side') {
            this.resizeAnimation = this.resizeService.startResize(['content'], RESIZE_DELAY);
        }
    }

    onBackDropClicked() {
        this.menuService.hideTree();
        this.resizeService.resize(['content']);
    }

    private checkWidth() {
        if (window.innerWidth <= MEDIUM_WIDTH_SIZE && this.sideNavMode.value !== 'over') {
            this.menuService.hideTree();
            this.sideNavMode.next('over');
            this.resizeService.resize(['content']);
        } else if (window.innerWidth > MEDIUM_WIDTH_SIZE && this.sideNavMode.value !== 'side') {
            this.sideNavMode.next('side');
            this.resizeService.resize(['content']);
        }
    }
}
